import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api, { API_BASE } from '../../lib/api';
import { useDeepTranslate } from '../../lib/useDeepTranslate';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CameraCapture from '../../components/CameraCapture';
import TripRouteMap from '../../components/TripRouteMap';
import { MapPin, Phone, Plus, Camera, ArrowCounterClockwise, Package, ClockCountdown, Truck as TruckIcon, CheckCircle, Warning, ArrowUp, ArrowDown } from '@phosphor-icons/react';

const STATUS_STEPS = ['allocated', 'accepted', 'in_progress', 'pickup_reached', 'pickup_done', 'drop_reached', 'completed'];

const DELAY_REASONS = [
  { key: 'material_not_ready', icon: Package, color: 'border-[#FF8C00] bg-[#FF8C00]/5' },
  { key: 'dc_delay', icon: ClockCountdown, color: 'border-[#FF3333] bg-[#FF3333]/5' },
  { key: 'loading_delay', icon: TruckIcon, color: 'border-[#002FA7] bg-[#002FA7]/5' },
  { key: 'reached_early', icon: CheckCircle, color: 'border-[#00C853] bg-[#00C853]/5' },
  { key: 'others', icon: Warning, color: 'border-[#808080] bg-[#808080]/5' },
];

export default function PilotTrips() {
  const { t } = useTranslation();
  const tr = useDeepTranslate();
  const { user } = useAuth();
  const canSelfAddTrip = user?.can_self_add_trip !== false;
  const [trips, setTrips] = useState([]);
  // Phase 78i — three-tab split: Allocated / In Progress / Completed.
  // Default to "allocated" because that's where pilots primarily act.
  const [tab, setTab] = useState('allocated');
  // Phase 78j — "↻ Live" indicator. Briefly turns solid green after
  // every successful fetch so pilots can see the queue is in sync.
  const [syncFlash, setSyncFlash] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  // Pilot's primary + secondary vehicle (for the Veh 1 / Veh 2 sub-tabs).
  const [vehicles, setVehicles] = useState({ primary: null, secondary: null });
  const [vehicleFilter, setVehicleFilter] = useState('primary'); // 'primary' | 'secondary'
  const [actionDialog, setActionDialog] = useState(null);
  const [actionForm, setActionForm] = useState({ km_reading: '', lat: 0, lng: 0 });
  const [selfTripDialog, setSelfTripDialog] = useState(false);
  const [selfForm, setSelfForm] = useState({ pickup_supplier_name: '', pickup_location: '', drop_supplier_name: '', drop_location: '', start_km: '' });
  const [showCamera, setShowCamera] = useState(null);
  const [showDcCamera, setShowDcCamera] = useState(null); // {tripId, label}
  const [capturedPhotos, setCapturedPhotos] = useState({});

  // Confirmation state
  const [confirmDialog, setConfirmDialog] = useState(null);
  // Delay reason state
  const [delayDialog, setDelayDialog] = useState(null);
  const [selectedDelay, setSelectedDelay] = useState('');
  const [delayNote, setDelayNote] = useState('');
  // KM validation error
  const [kmError, setKmError] = useState('');
  // Last finished KM for guide
  const [lastKm, setLastKm] = useState({ last_end_km: 0, last_trip: '' });

  // ----- Live duration counters (ticks every 60s) -----
  // `now` re-renders every minute so durations stay current without refetch.
  const [now, setNow] = useState(() => Date.now());
  const [activeBreak, setActiveBreak] = useState(null);

  // Phase 78ag — new-extra-stop notification state. We snapshot the
  // extras count per trip across fetches and pop a dismissible banner +
  // beep whenever booking/admin adds a stop while pilot is on the job.
  const prevExtrasRef = useRef({});
  const [newStopBanners, setNewStopBanners] = useState([]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Poll active break alongside the minute tick so the on-break banner updates.
  const fetchActiveBreak = useCallback(async () => {
    try {
      const { data } = await api.get('/pilot/breaks');
      const ab = Array.isArray(data) ? data.find(b => !b.end_time) : null;
      setActiveBreak(ab || null);
    } catch { setActiveBreak(null); }
  }, []);
  useEffect(() => { fetchActiveBreak(); }, [fetchActiveBreak, now]);

  // "2h 14m" / "45m" / "<1m"
  const formatLiveDuration = (startIso) => {
    if (!startIso) return '';
    const start = new Date(startIso).getTime();
    if (Number.isNaN(start)) return '';
    const mins = Math.max(0, Math.floor((now - start) / 60000));
    if (mins < 1) return '<1m';
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };

  const fetchTrips = useCallback(async () => {
    try {
      const { data } = await api.get('/pilot/trips', { params: { status: tab } });
      // Phase 78ag — detect newly-added extra stops and surface a banner
      // + soft beep. We compare each trip's extras count vs the last
      // snapshot held in `prevExtrasRef`.
      const next = {};
      const newly = [];
      for (const t of (data || [])) {
        const ep = (t.extra_pickups || []).length;
        const ed = (t.extra_drops || []).length;
        next[t.id] = { ep, ed };
        const prev = prevExtrasRef.current[t.id];
        if (prev && (ep > prev.ep || ed > prev.ed)) {
          newly.push({
            tripId: t.id,
            label: t.pickup_supplier_name + ' → ' + t.drop_supplier_name,
            added_pickups: ep - prev.ep,
            added_drops: ed - prev.ed,
          });
        }
      }
      prevExtrasRef.current = next;
      if (newly.length > 0) {
        setNewStopBanners(b => [...b, ...newly.map(n => ({ ...n, at: Date.now() }))]);
        try {
          // Soft beep via WebAudio so we don't need an asset file.
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.connect(g); g.connect(ctx.destination);
          o.type = 'sine'; o.frequency.value = 880;
          g.gain.setValueAtTime(0.15, ctx.currentTime);
          g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
          o.start(); o.stop(ctx.currentTime + 0.5);
        } catch {}
      }
      setTrips(data);
      // Phase 78j — visual confirmation that the queue just synced.
      setLastSyncAt(Date.now());
      setSyncFlash(true);
      setTimeout(() => setSyncFlash(false), 1100);
    } catch {}
  }, [tab]);

  // Phase 78ag — auto-dismiss new-stop banners after 12 seconds.
  useEffect(() => {
    if (newStopBanners.length === 0) return undefined;
    const id = setTimeout(() => {
      setNewStopBanners(arr => arr.filter(b => Date.now() - b.at < 12000));
    }, 13000);
    return () => clearTimeout(id);
  }, [newStopBanners]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  // Phase 78i — auto-refresh every 20s so admin priority changes appear
  // on the pilot's screen without manual reload.
  useEffect(() => {
    const id = setInterval(fetchTrips, 20000);
    return () => clearInterval(id);
  }, [fetchTrips]);

  // Fetch last KM on mount
  useEffect(() => {
    api.get('/pilot/last-km').then(r => setLastKm(r.data)).catch(() => {});
  }, []);

  // Fetch vehicles attached to the pilot for the Veh-1 / Veh-2 sub-tabs.
  const fetchVehicles = useCallback(async () => {
    try {
      const { data } = await api.get('/pilot/dashboard');
      setVehicles({ primary: data?.vehicle || null, secondary: data?.secondary_vehicle || null });
    } catch {}
  }, []);
  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const handleSwitchVehicle = async (tripId, target) => {
    try {
      await api.put(`/pilot/trips/${tripId}/switch-vehicle`, { target });
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Switch failed');
    }
  };

  const handleTripAction = async (tripId, action, extraBody = {}) => {
    try {
      // Phase 66: KM is no longer collected per trip — don't forward actionForm.km_reading.
      const { km_reading: _removedKm, ...rest } = actionForm;
      void _removedKm;
      const body = { ...rest, ...extraBody };
      // Phase 78at — Always attach the live pilot GPS fix on every
      // button tap so the backend can backfill missing pickup/drop
      // coords AND stamp the matching google_* timestamp. The simple
      // confirmation flow (Pickup Reached / Drop Reached) never opened
      // the KM dialog, so actionForm.lat/lng stayed 0 and the trip's
      // coords + google timings remained empty.
      if (pilotPos?.lat && pilotPos?.lng && (!body.lat || !body.lng)) {
        body.lat = pilotPos.lat;
        body.lng = pilotPos.lng;
      }
      const attach = (slot, photoKey, gpsKey) => {
        const entry = capturedPhotos[`${tripId}_${slot}`];
        if (!entry) return;
        if (typeof entry === 'string') {
          body[photoKey] = entry;
        } else {
          body[photoKey] = entry.url;
          if (entry.gps) body[gpsKey] = entry.gps;
        }
      };
      attach('goods', 'goods_photo', 'goods_photo_gps');
      attach('unload', 'unload_photo', 'unload_photo_gps');
      attach('receipt', 'receipt_photo', 'receipt_photo_gps');
      attach('km', 'km_photo', 'km_photo_gps');

      const { data } = await api.put(`/pilot/trips/${tripId}/${action}`, body);

      // After pickup-done / drop-done, check if there was a delay (>20 min waiting)
      if (action === 'pickup-done' && data.waiting_minutes > 20) {
        setDelayDialog({ tripId, waitingMinutes: data.waiting_minutes, stage: 'pickup' });
      } else if (action === 'drop-done' && data.waiting_minutes > 20) {
        setDelayDialog({ tripId, waitingMinutes: data.waiting_minutes, stage: 'drop' });
      }

      // If pickup_done returned needs_dc_capture (direct trip with no DC yet),
      // pop up the DC camera so the pilot can attach the DC right away.
      if (action === 'pickup-done' && data.needs_dc_capture) {
        setShowDcCamera({ tripId, label: 'Attach DC Photo' });
      }

      fetchTrips();
      setActionDialog(null);
      setConfirmDialog(null);
      setActionForm({ km_reading: '', lat: 0, lng: 0 });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleReverse = async (tripId) => {
    try {
      await api.put(`/pilot/trips/${tripId}/reverse`);
      fetchTrips();
      setConfirmDialog(null);
    } catch (err) { alert(err.response?.data?.detail || 'Cannot undo'); }
  };

  const handleReorder = async (tripId, direction) => {
    // Reorder only allocated/accepted trips
    const queuedTrips = trips.filter(t => ['allocated', 'accepted'].includes(t.status));
    const idx = queuedTrips.findIndex(t => t.id === tripId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= queuedTrips.length) return;
    // Swap
    const reordered = [...queuedTrips];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    try {
      await api.put('/pilot/trips/reorder', { trip_ids: reordered.map(t => t.id) });
      fetchTrips();
    } catch (err) { alert(err.response?.data?.detail || 'Reorder failed'); }
  };

  const handleDelaySubmit = async () => {
    if (!delayDialog || !selectedDelay) return;
    try {
      if (delayDialog.stage === 'drop') {
        await api.put(`/pilot/trips/${delayDialog.tripId}/drop-done`, {
          drop_delay_reason: selectedDelay,
          drop_delay_note: delayNote,
        });
      } else {
        await api.put(`/pilot/trips/${delayDialog.tripId}/pickup-done`, {
          pickup_delay_reason: selectedDelay,
          pickup_delay_note: delayNote,
        });
      }
    } catch {}
    setDelayDialog(null);
    setSelectedDelay('');
    setDelayNote('');
  };

  const handleSelfTrip = async (e) => {
    e.preventDefault();
    try {
      await api.post('/pilot/trips/self-create', { ...selfForm });
      fetchTrips();
      setSelfTripDialog(false);
      setSelfForm({ pickup_supplier_name: '', pickup_location: '', drop_supplier_name: '', drop_location: '', start_km: '' });
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handlePhotoCapture = async (captureData, tripId, photoType) => {
    const restore = showCamera?.restoreDialog || null;
    try {
      const { data } = await api.post('/upload/photo-base64', {
        photo: captureData.photo,
        gps: captureData.gps,
        label: photoType
      });
      setCapturedPhotos(prev => ({
        ...prev,
        [`${tripId}_${photoType}`]: { url: data.url, gps: captureData.gps || null }
      }));
    } catch {}
    setShowCamera(null);
    // Re-open the action dialog so the user can confirm KM + submit.
    if (restore) setActionDialog(restore);
  };

  const handleDcCapture = async (captureData) => {
    const tripId = showDcCamera?.tripId;
    setShowDcCamera(null);
    if (!tripId) return;
    try {
      const { data: up } = await api.post('/upload/photo-base64', {
        photo: captureData.photo,
        gps: captureData.gps,
        label: 'dc',
      });
      await api.post(`/pilot/trips/${tripId}/dc-photo`, {
        photo_url: up.url,
        photo_gps: captureData.gps || null,
      });
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to attach DC');
    }
  };

  // Phase 78aa — Per-pooled-child step transition (only when child's
  // pickup or drop differs from the lead trip). Tries to grab a GPS
  // fix opportunistically but doesn't block on it.
  const pooledStep = async (leadId, childId, step) => {
    if (!childId) return;
    let pos = null;
    if (navigator.geolocation) {
      try {
        pos = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (p) => resolve(p),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 4000, maximumAge: 5000 },
          );
        });
      } catch {}
    }
    try {
      await api.put(`/pilot/trips/${leadId}/pooled/${childId}/step`, {
        step,
        lat: pos?.coords?.latitude || 0,
        lng: pos?.coords?.longitude || 0,
      });
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Step update failed');
    }
  };

  // Phase 78ae — Mark an extra pickup/drop stop as reached or done.
  const extraStopStep = async (tripId, stopId, step) => {
    try {
      await api.put(`/trips/${tripId}/extra-stop/${stopId}/step`, { step });
      fetchTrips();
    } catch (err) {
      alert(err.response?.data?.detail || 'Step update failed');
    }
  };

  // Phase 78as — Geofence-gated "Done" buttons. We keep a single shared
  // pilot GPS fix that refreshes every 12s while a trip is in progress.
  // Each "Done" button on the multi-stop layout is enabled only when the
  // pilot is within `GEOFENCE_M` of the stop's coords (default 150m to
  // match the backend `GOOGLE_GEOFENCE_RADIUS_M`). "Reached" stays
  // tap-anywhere so the pilot can pre-announce arrival.
  const GEOFENCE_M = 150;
  const [pilotPos, setPilotPos] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return undefined;
    let stopped = false;
    const refresh = () => {
      navigator.geolocation.getCurrentPosition(
        (p) => { if (!stopped) setPilotPos({ lat: p.coords.latitude, lng: p.coords.longitude, acc: p.coords.accuracy, at: Date.now() }); },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 6000 },
      );
    };
    refresh();
    const id = setInterval(refresh, 12000);
    return () => { stopped = true; clearInterval(id); };
  }, []);
  // Haversine distance in metres. Returns Infinity if either coord is
  // missing so the gate "fails open" (button stays disabled) rather
  // than silently allowing a far-away tap.
  const distM = (lat1, lng1, lat2, lng2) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
    const toRad = (x) => x * Math.PI / 180;
    const R = 6371000;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };
  // Returns {near: bool, distM: number} from current pilotPos to a stop.
  // Pilot's GPS reading also has an `acc` field (uncertainty in metres);
  // we ADD it to the threshold so noisy fixes don't lock the pilot out
  // when they're genuinely standing right at the gate.
  const proximityTo = (lat, lng) => {
    if (!pilotPos) return { near: false, distM: Infinity, hasFix: false };
    const d = distM(pilotPos.lat, pilotPos.lng, lat, lng);
    const allowance = GEOFENCE_M + Math.min(pilotPos.acc || 0, 100);
    return { near: d <= allowance, distM: d, hasFix: true };
  };

  const STATUS_LABEL = {
    allocated: t('allocated'), accepted: t('accepted'), in_progress: t('in_progress'),
    pickup_reached: t('pickup_reached'), pickup_done: t('pickup_done'),
    drop_reached: t('drop_reached'), completed: t('completed')
  };

  const getNextAction = (trip) => {
    const actions = {
      // Trips are auto-accepted on allocation. If a stale 'allocated' trip
      // still exists (legacy data), let the pilot start it directly — no
      // accept step. Pilot must call admin to refuse a trip.
      allocated: { action: 'start', label: t('start_trip'), color: 'bg-[#002FA7]', needsKm: true, photoType: 'km' },
      accepted: { action: 'start', label: t('start_trip'), color: 'bg-[#002FA7]', needsKm: true, photoType: 'km' },
      in_progress: { action: 'pickup-reached', label: t('pickup_reached'), color: 'bg-[#FFD700] text-[#0A0A0A]' },
      pickup_reached: { action: 'pickup-done', label: t('pickup_done'), color: 'bg-[#FF8C00]', photoType: 'goods' },
      pickup_done: { action: 'drop-reached', label: t('drop_reached'), color: 'bg-[#FFD700] text-[#0A0A0A]' },
      drop_reached: { action: 'drop-done', label: t('unloading_done'), color: 'bg-[#00C853]', needsKm: true, photoType: 'unload' },
    };
    return actions[trip.status];
  };

  // Can this trip status be reversed?
  const canReverse = (status) => ['in_progress', 'pickup_reached', 'pickup_done', 'drop_reached'].includes(status);

  // Show confirmation before any action
  const requestConfirm = (trip, nextAction) => {
    // Phase 72: block Start Trip until a vehicle is attached.
    if (nextAction.action === 'start' && !vehicles.primary && !vehicles.secondary) {
      alert('No vehicle assigned.\nPlease scan a vehicle QR and enter Start KM before starting any trip.');
      return;
    }
    if (nextAction.needsKm || nextAction.photoType) {
      // Open the KM/photo dialog first, confirmation happens on submit
      // Pass startKm for end KM validation
      setActionDialog({ ...nextAction, tripId: trip.id, startKm: trip.start_km || 0 });
    } else {
      // Simple confirmation
      setConfirmDialog({ type: 'action', tripId: trip.id, ...nextAction });
    }
  };

  return (
    <div data-testid="pilot-trips" className="space-y-6">
      {/* Camera Overlay */}
      {showCamera && (
        <CameraCapture
          label={showCamera.label}
          onCapture={(data) => handlePhotoCapture(data, showCamera.tripId, showCamera.type)}
          onClose={() => {
            const restore = showCamera?.restoreDialog || null;
            setShowCamera(null);
            // Restore the underlying action dialog (if any) so the user can
            // continue entering KM reading / confirming the action.
            if (restore) setActionDialog(restore);
          }}
        />
      )}

      {/* DC Photo Camera Overlay */}
      {showDcCamera && (
        <CameraCapture
          label={showDcCamera.label}
          onCapture={handleDcCapture}
          onClose={() => setShowDcCamera(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">{t('my_trips')}</h2>
        {canSelfAddTrip && (
          <Button onClick={() => setSelfTripDialog(true)} className="bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full" data-testid="self-trip-btn">
            <Plus size={16} className="mr-1" /> {t('add_trip')}
          </Button>
        )}
      </div>

      {/* Phase 78ag — Toasts when booking/admin adds a new stop mid-trip.
          Stacked top-right, auto-dismiss after 12s, manually closable. */}
      {newStopBanners.length > 0 && (
        <div className="fixed top-3 right-3 z-50 flex flex-col gap-2 max-w-[88vw] w-[320px]" data-testid="pilot-new-stop-banners">
          {newStopBanners.map((b, idx) => (
            <div
              key={`${b.tripId}-${b.at}-${idx}`}
              className="rounded-xl bg-[#7C3AED] text-white shadow-lg px-3 py-2.5 flex items-start gap-2 animate-pulse"
              data-testid={`pilot-new-stop-banner-${idx}`}
            >
              <Plus size={18} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-80">New stop added</p>
                <p className="text-sm font-bold truncate">{b.label}</p>
                <p className="text-[11px] opacity-90">
                  {b.added_pickups > 0 && <>+{b.added_pickups} pickup{b.added_pickups > 1 ? 's' : ''} </>}
                  {b.added_drops > 0 && <>+{b.added_drops} drop{b.added_drops > 1 ? 's' : ''}</>}
                </p>
              </div>
              <button
                onClick={() => setNewStopBanners(arr => arr.filter((_, i) => i !== idx))}
                className="text-white/80 hover:text-white text-lg leading-none"
                data-testid={`pilot-new-stop-banner-${idx}-close`}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* On-break banner — live duration ticks every 60s */}
      {activeBreak && (
        <div
          className="rounded-2xl border border-[#FF8C00]/30 bg-[#FF8C00]/10 px-3 py-2 flex items-center justify-between gap-2"
          data-testid="pilot-on-break-banner"
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-2 h-2 rounded-full bg-[#FF8C00] animate-pulse flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF8C00]">On Break</div>
              <div className="text-xs text-[#0A0A0A] capitalize truncate">{activeBreak.break_type?.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <span
            className="text-sm font-mono font-bold text-[#FF8C00] whitespace-nowrap"
            data-testid="pilot-on-break-duration"
          >
            {formatLiveDuration(activeBreak.start_time)}
          </span>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full bg-[#F3F3F3] border border-[#E5E5E5] w-full p-1">
          <TabsTrigger value="allocated" className="rounded-full flex-1 text-[10px] uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow" data-testid="tab-allocated">{t('allocated')}</TabsTrigger>
          <TabsTrigger value="in_progress" className="rounded-full flex-1 text-[10px] uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow" data-testid="tab-in-progress">{t('in_progress')}</TabsTrigger>
          <TabsTrigger value="completed" className="rounded-full flex-1 text-[10px] uppercase tracking-widest data-[state=active]:bg-white data-[state=active]:shadow" data-testid="tab-completed">{t('completed')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Phase 78j — live-sync indicator. Flashes solid green for ~1 s
          after each successful poll (every 20 s); otherwise shows a
          faint pulse so pilots always know the screen is "live". */}
      <button
        type="button"
        onClick={fetchTrips}
        className={`flex items-center gap-1.5 mx-auto text-[10px] uppercase tracking-widest font-bold transition-colors ${
          syncFlash ? 'text-[#00C853]' : 'text-[#999]'
        }`}
        data-testid="pilot-trips-live-sync"
        title={lastSyncAt ? `Last synced: ${new Date(lastSyncAt).toLocaleTimeString()}` : 'Sync now'}
      >
        <span className={`inline-block w-2 h-2 rounded-full ${syncFlash ? 'bg-[#00C853]' : 'bg-[#999] animate-pulse'}`} />
        {syncFlash ? 'Synced' : 'Live updates'}
      </button>

      {/* Veh 1 Trip / Veh 2 Trip — only when secondary vehicle is attached */}
      {vehicles.secondary && (
        <div className="flex bg-[#F3F3F3] border border-[#E5E5E5] rounded-full p-1" data-testid="vehicle-filter">
          {[
            { key: 'primary', label: 'Veh 1 Trip', vnum: vehicles.primary?.vehicle_number, count: trips.filter(tr => tr.vehicle_id === vehicles.primary?.id).length },
            { key: 'secondary', label: 'Veh 2 Trip', vnum: vehicles.secondary?.vehicle_number, count: trips.filter(tr => tr.vehicle_id === vehicles.secondary?.id).length },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setVehicleFilter(opt.key)}
              data-testid={`veh-filter-${opt.key}`}
              className={`flex-1 px-3 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold transition-all ${
                vehicleFilter === opt.key ? 'bg-white shadow text-[#002FA7]' : 'text-[#666]'
              }`}
            >
              {opt.label}{opt.vnum ? ` · ${opt.vnum}` : ''}{opt.count > 0 ? ` (${opt.count})` : ''}
            </button>
          ))}
        </div>
      )}

      {/* Self Trip Dialog */}
      <Dialog open={selfTripDialog} onOpenChange={setSelfTripDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('add_trip')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSelfTrip} className="space-y-4 mt-4">
            <div><Label className="text-xs uppercase tracking-widest">{t('pickup_location')}</Label><Input value={selfForm.pickup_supplier_name} onChange={e => setSelfForm({...selfForm, pickup_supplier_name: e.target.value})} required className="rounded-lg" /></div>
            <div><Label className="text-xs uppercase tracking-widest">{t('drop_location')}</Label><Input value={selfForm.drop_supplier_name} onChange={e => setSelfForm({...selfForm, drop_supplier_name: e.target.value})} required className="rounded-lg" /></div>
            {/* Phase 66: Start KM removed — inherited from vehicle session */}
            <Button type="submit" className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 hover:text-white rounded-full">{t('create')} & {t('start_trip')}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action Dialog (KM / Photo entry) — includes confirm */}
      <Dialog
        open={!!actionDialog}
        onOpenChange={(open) => {
          if (!open) {
            if (showCamera) return;
            setActionDialog(null); setKmError('');
          }
        }}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => { if (showCamera) e.preventDefault(); }}
        >
          <DialogHeader><DialogTitle>{actionDialog?.label}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Phase 66: KM entry removed from trip actions — it's now captured at
                vehicle scan/leave. Photo capture below is still required. */}
            {actionDialog?.photoType && (() => {
              const captured = capturedPhotos[`${actionDialog?.tripId}_${actionDialog?.photoType}`];
              const photoUrl = captured ? (typeof captured === 'string' ? captured : captured.url) : null;
              const photoSrc = photoUrl ? (photoUrl.startsWith('/') ? `${API_BASE}${photoUrl}` : photoUrl) : null;
              return (
                <div>
                  <Label className="text-xs uppercase tracking-widest">{t('take_photo')}</Label>
                  <div className="flex items-center gap-3 mt-2">
                    {photoSrc && (
                      <img
                        src={photoSrc}
                        alt="Captured"
                        className="w-20 h-20 object-cover border border-[#00C853]"
                        data-testid="trip-captured-thumb"
                      />
                    )}
                    <button
                      onClick={() => {
                        // Stash the current action state, CLOSE the dialog, then
                        // open the camera. This guarantees the camera doesn't
                        // render underneath a Radix Dialog — which was causing
                        // the shutter tap to be intercepted on some Android
                        // builds. The pickup user flow never hits this bug
                        // because it never opens a Dialog before the camera.
                        const restore = actionDialog;
                        setShowCamera({
                          tripId: actionDialog.tripId,
                          type: actionDialog.photoType,
                          label: actionDialog.label,
                          restoreDialog: restore,
                        });
                        setActionDialog(null);
                      }}
                      className={`flex items-center gap-2 border border-dashed p-3 transition-colors text-sm ${photoSrc ? 'border-[#00C853] text-[#00C853]' : 'border-[#E5E5E5] hover:border-[#002FA7]'}`}
                      data-testid="trip-camera-btn"
                    >
                      <Camera size={18} className={photoSrc ? 'text-[#00C853]' : 'text-[#002FA7]'} />
                      {photoSrc ? 'Retake' : t('capture')}
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Confirm section */}
            <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 p-3 text-center">
              <p className="text-sm font-bold">{t('are_you_sure')}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setActionDialog(null); setKmError(''); }}
                className="py-3 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3]"
                data-testid="action-no-btn"
              >
                {t('no')}
              </button>
              <button
                onClick={() => {
                  // Phase 66: KM validation removed from trip actions — KM is
                  // now captured at scan/leave time. Photo still required.
                  handleTripAction(actionDialog.tripId, actionDialog.action);
                }}
                className="py-3 text-sm font-bold uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
                data-testid="action-yes-btn"
              >
                {t('yes')}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simple Confirm Dialog (for actions without KM/photo) */}
      {confirmDialog?.type === 'action' && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm p-6 space-y-5 text-center">
            <p className="text-lg font-bold">{t('are_you_sure')}</p>
            <p className="text-base font-medium text-[#002FA7]">{confirmDialog.label}</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="py-4 text-base font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3]"
                data-testid="confirm-no-btn"
              >
                {t('no')}
              </button>
              <button
                onClick={() => handleTripAction(confirmDialog.tripId, confirmDialog.action)}
                className="py-4 text-base font-bold uppercase tracking-widest bg-[#002FA7] text-white hover:bg-[#002FA7]/90"
                data-testid="confirm-yes-btn"
              >
                {t('yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reverse Confirm Dialog */}
      {confirmDialog?.type === 'reverse' && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm p-6 space-y-5 text-center">
            <ArrowCounterClockwise size={40} className="mx-auto text-[#FF8C00]" />
            <p className="text-lg font-bold">{t('are_you_sure')}</p>
            <p className="text-sm text-[#666]">{t('undo_last')}</p>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="py-4 text-base font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3]"
                data-testid="reverse-no-btn"
              >
                {t('no')}
              </button>
              <button
                onClick={() => handleReverse(confirmDialog.tripId)}
                className="py-4 text-base font-bold uppercase tracking-widest bg-[#FF8C00] text-white hover:bg-[#FF8C00]/90"
                data-testid="reverse-yes-btn"
              >
                {t('yes')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delay Reason Dialog — big buttons for drivers */}
      {delayDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center sm:items-center p-0 sm:p-4">
          <div
            className="bg-white w-full max-w-md sm:rounded-t-none rounded-t-2xl sm:rounded-2xl flex flex-col"
            style={{ maxHeight: '90vh' }}
            data-testid="delay-reason-dialog"
          >
            {/* Scrollable content */}
            <div className="overflow-y-auto p-5 space-y-4 flex-1">
              <div className="text-center">
                <ClockCountdown size={36} className="mx-auto text-[#FF3333] mb-2" />
                <p className="text-lg font-bold">{t('delay_reason')}</p>
                <p className="text-sm text-[#666]">
                  {delayDialog.waitingMinutes} min {delayDialog.stage === 'drop' ? t('drop_reached') : t('pickup_done')}
                </p>
              </div>

              <div className="space-y-2">
                {DELAY_REASONS.map(r => (
                  <button
                    key={r.key}
                    onClick={() => setSelectedDelay(r.key)}
                    className={`w-full flex items-center gap-4 p-4 border-2 transition-all ${
                      selectedDelay === r.key
                        ? 'border-[#002FA7] bg-[#002FA7]/5 ring-2 ring-[#002FA7]/20'
                        : r.color
                    }`}
                    data-testid={`delay-${r.key}`}
                  >
                    <r.icon size={28} weight="bold" className={selectedDelay === r.key ? 'text-[#002FA7]' : 'text-[#666]'} />
                    <span className={`text-base font-medium ${selectedDelay === r.key ? 'text-[#002FA7]' : ''}`}>{t(r.key)}</span>
                  </button>
                ))}
              </div>

              {selectedDelay === 'others' && (
                <Input
                  value={delayNote}
                  onChange={e => setDelayNote(e.target.value)}
                  placeholder="Type reason..."
                  className="rounded-full text-base py-3"
                />
              )}
            </div>

            {/* Sticky action buttons so Confirm is always reachable even on
                small Android viewports with the soft keyboard open. */}
            <div className="grid grid-cols-2 gap-3 p-5 pt-3 border-t border-[#E5E5E5] bg-white">
              <button
                onClick={() => { setDelayDialog(null); setSelectedDelay(''); setDelayNote(''); }}
                className="py-3 text-sm font-bold uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3]"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleDelaySubmit}
                disabled={!selectedDelay}
                className={`py-3 text-sm font-bold uppercase tracking-widest text-white ${selectedDelay ? 'bg-[#002FA7] hover:bg-[#002FA7]/90' : 'bg-[#E5E5E5] cursor-not-allowed'}`}
                data-testid="delay-submit-btn"
              >
                {t('confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trip List */}
      <div className="space-y-3">
        {(() => {
          const filtered = vehicles.secondary
            ? trips.filter(tr => {
                const onSec = vehicles.secondary && tr.vehicle_id === vehicles.secondary.id;
                if (vehicleFilter === 'secondary') return onSec;
                // primary tab: show trips on the primary vehicle, plus any trip
                // that has no vehicle assigned yet (legacy / pending).
                return !onSec;
              })
            : trips;
          // Only show the "no trips on this vehicle" message when an active
          // vehicle filter is hiding items (i.e., secondary vehicle attached
          // AND trips exist on the other vehicle). Otherwise the generic
          // "No data found" empty state below handles it.
          if (filtered.length === 0) {
            if (vehicles.secondary && trips.length > 0) {
              return (
                <div className="text-center text-[#666] text-sm py-8" data-testid="no-trips-for-vehicle">
                  No trips on this vehicle.
                </div>
              );
            }
            return null;
          }
          return filtered.map((trip, idx) => {
          const nextAction = getNextAction(trip);
          const isQueued = ['allocated', 'accepted'].includes(trip.status);
          const queueNum = isQueued ? idx + 1 : null;
          // Phase 78ar — Multi-stop pilot layout. When a trip has any
          // extra pickups OR drops, swap the bottom-button flow for
          // per-stop "Reached / Done" buttons inline, plus a single
          // top-of-card "Start Trip" button before the trip starts and
          // a final "All Completed" button at the end. The single-stop
          // flow stays intact for rollback.
          const hasExtras = (trip.extra_pickups?.length || 0) + (trip.extra_drops?.length || 0) > 0;
          const stepNotPickupReached = !trip.pickup_reached_at && ['in_progress'].includes(trip.status);
          const stepPickupReachedNotDone = !!trip.pickup_reached_at && !trip.pickup_done_at && trip.status === 'pickup_reached';
          const stepNotDropReached = !!trip.pickup_done_at && !trip.drop_reached_at && trip.status === 'pickup_done';
          const stepDropReachedNotDone = trip.status === 'drop_reached';
          return (
            <div key={trip.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-3 space-y-2 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {queueNum && <span className="w-5 h-5 rounded-full bg-[#002FA7] text-white text-[10px] font-bold flex items-center justify-center">{queueNum}</span>}
                  <span className="font-mono text-xs text-[#666]">#{trip.id}</span>
                  {trip.self_created && <span className="text-[10px] rounded-full bg-[#FF8C00]/10 text-[#FF8C00] px-2 py-0.5">Self</span>}
                  {trip.priority && <span className={`text-[10px] rounded-full px-2 py-0.5 ${trip.priority === 'shortage' ? 'bg-[#FF3333]/10 text-[#FF3333]' : 'bg-[#808080]/10 text-[#808080]'}`}>{trip.priority}</span>}
                  {trip.allocation_type === 'auto_bunched' ? (
                    <span
                      className="text-[10px] rounded-full px-2 py-0.5 bg-[#A855F7]/10 text-[#A855F7] font-bold flex items-center gap-1"
                      title={`This trip joined an existing pickup at ${trip.pickup_supplier_name || 'the same supplier'} — combined with another booking already heading there.`}
                      data-testid={`pilot-trip-${trip.id}-bunched`}
                    >
                      ✚ Bunched
                    </span>
                  ) : trip.allocation_type ? (
                    <span className="text-[9px] rounded-full px-1.5 py-0.5 bg-[#002FA7]/10 text-[#002FA7] uppercase">{trip.allocation_type}</span>
                  ) : null}
                  {trip.pickup_flow && trip.dc_ready && (
                    <span className="text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-[#00C853]/10 text-[#00C853] font-bold flex items-center gap-1" title="Goods ready with DC at pickup location" data-testid={`pilot-trip-${trip.id}-dc-chip`}>
                      ✓ DC
                    </span>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-[10px] uppercase tracking-widest font-medium text-[#002FA7] whitespace-nowrap">{STATUS_LABEL[trip.status] || trip.status}</span>
                  {trip.status === 'pickup_reached' && trip.pickup_reached_at && (
                    <span
                      className="text-[10px] font-mono font-bold whitespace-nowrap rounded-full px-2 py-0.5 bg-[#FFD700]/20 text-[#A07000] border border-[#FFD700]/40"
                      title={`Waiting at pickup since ${new Date(trip.pickup_reached_at).toLocaleTimeString()}`}
                      data-testid={`pilot-trip-${trip.id}-pickup-wait-timer`}
                    >
                      ⏳ Pickup wait · {formatLiveDuration(trip.pickup_reached_at)}
                    </span>
                  )}
                  {trip.status === 'drop_reached' && trip.drop_reached_at && (
                    <span
                      className="text-[10px] font-mono font-bold whitespace-nowrap rounded-full px-2 py-0.5 bg-[#FFD700]/20 text-[#A07000] border border-[#FFD700]/40"
                      title={`Waiting at drop since ${new Date(trip.drop_reached_at).toLocaleTimeString()}`}
                      data-testid={`pilot-trip-${trip.id}-drop-wait-timer`}
                    >
                      ⏳ Drop wait · {formatLiveDuration(trip.drop_reached_at)}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#00C853]" />
                  <div className="w-px h-6 bg-[#E5E5E5]" />
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FF3333]" />
                </div>
                <div className="flex-1 min-w-0">
                  {/* Phase 78z — main trip pickup/drop in BOLD so it's
                      visually distinct from the italic+underlined pooled
                      children below. */}
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-base font-bold truncate" data-testid={`pilot-trip-${trip.id}-main-pickup`}>
                      {hasExtras && <span className="font-mono text-[10px] text-[#00C853] mr-1 align-middle">PICKUP 1</span>}
                      {tr(trip.pickup_supplier_name) || tr(trip.pickup_location) || t('pickup_location')}
                    </p>
                    {/* Phase 78ar — Inline Reached/Done buttons on main pickup
                        when the trip has extras. Drives the canonical
                        pickup-reached → pickup-done state machine. */}
                    {hasExtras && stepNotPickupReached && (
                      <button
                        onClick={() => requestConfirm(trip, { action: 'pickup-reached', label: 'Pickup Reached', color: 'bg-[#FFD700] text-[#0A0A0A]' })}
                        className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm flex-shrink-0"
                        data-testid={`pilot-trip-${trip.id}-main-pickup-reached`}
                      >Reached</button>
                    )}
                    {hasExtras && stepPickupReachedNotDone && (() => {
                      const prox = proximityTo(trip.pickup_lat, trip.pickup_lng);
                      return (
                        <button
                          onClick={() => requestConfirm(trip, { action: 'pickup-done', label: 'Pickup Done', color: 'bg-[#FF8C00]', photoType: 'goods' })}
                          disabled={!prox.near}
                          title={!prox.near ? `Move closer — you're ${prox.hasFix ? Math.round(prox.distM) + 'm' : '?'} from the pickup (must be within ${GEOFENCE_M}m)` : 'Mark pickup done'}
                          className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm flex-shrink-0 ${prox.near ? 'bg-[#FF8C00] text-white' : 'bg-[#CCCCCC] text-[#666] cursor-not-allowed'}`}
                          data-testid={`pilot-trip-${trip.id}-main-pickup-done`}
                        >{prox.near ? 'Done' : '🔒 Done'}</button>
                      );
                    })()}
                    {hasExtras && trip.pickup_done_at && (
                      <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full flex-shrink-0">✓ Done</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <p className="text-base font-bold truncate" data-testid={`pilot-trip-${trip.id}-main-drop`}>
                      {hasExtras && <span className="font-mono text-[10px] text-[#FF3333] mr-1 align-middle">DROP 1</span>}
                      {tr(trip.drop_supplier_name) || tr(trip.drop_location) || t('drop_location')}
                    </p>
                    {hasExtras && stepNotDropReached && (
                      <button
                        onClick={() => requestConfirm(trip, { action: 'drop-reached', label: 'Drop Reached', color: 'bg-[#FFD700] text-[#0A0A0A]' })}
                        className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm flex-shrink-0"
                        data-testid={`pilot-trip-${trip.id}-main-drop-reached`}
                      >Reached</button>
                    )}
                    {hasExtras && stepDropReachedNotDone && (() => {
                      const prox = proximityTo(trip.drop_lat, trip.drop_lng);
                      return (
                        <button
                          onClick={() => requestConfirm(trip, { action: 'drop-done', label: 'Drop Done', color: 'bg-[#00C853]', needsKm: true, photoType: 'unload' })}
                          disabled={!prox.near}
                          title={!prox.near ? `Move closer — you're ${prox.hasFix ? Math.round(prox.distM) + 'm' : '?'} from the drop (must be within ${GEOFENCE_M}m)` : 'Mark drop done'}
                          className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm flex-shrink-0 ${prox.near ? 'bg-[#00C853] text-white' : 'bg-[#CCCCCC] text-[#666] cursor-not-allowed'}`}
                          data-testid={`pilot-trip-${trip.id}-main-drop-done`}
                        >{prox.near ? 'Done' : '🔒 Done'}</button>
                      );
                    })()}
                    {hasExtras && trip.drop_done_at && (
                      <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full flex-shrink-0">✓ Done</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Phase 78ar — Top-of-card "Start Trip" button for multi-stop
                  trips. Renders only when trip is still allocated/accepted
                  AND has extras. The legacy big bottom Start Trip button
                  is suppressed in that case (see below). */}
              {hasExtras && nextAction && nextAction.action === 'start' && (
                <Button
                  onClick={() => requestConfirm(trip, nextAction)}
                  disabled={!vehicles.primary && !vehicles.secondary}
                  className="w-full bg-[#002FA7] text-white hover:bg-[#002FA7]/90 rounded-full h-11 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#AAAAAA]"
                  data-testid={`pilot-trip-${trip.id}-top-start-trip`}
                >
                  {nextAction.label}
                </Button>
              )}

              {(trip.item || trip.qty) && (
                <div className="flex items-center gap-2 text-sm text-[#0A0A0A] font-bold" data-testid={`pilot-trip-${trip.id}-main-item`}>
                  <span className="truncate">{tr(trip.item)} · {trip.qty} {tr(trip.uom)}</span>
                </div>
              )}

              {/* Phase 78z — Pooled child cards. User-requested styling:
                  italic + underline + 2 font sizes smaller than main +
                  distinct purple background card so the pilot can instantly
                  tell the lead trip (bold) from each pooled booking.
                  Full details (pickup, drop, item, qty, UOM, booker mobile,
                  booked-from, remarks) are shown for each child. */}
              {/* Phase 78ae — Extra pickup / drop stops added by booking
                  staff or admin after the trip was created. Italic +
                  underline + smaller font, same visual language as pooled
                  items so the pilot instantly spots the "added later"
                  bits. Per-stop Reach / Done buttons appear once the trip
                  is in_progress. */}
              {(['extra_pickups', 'extra_drops'].some(k => Array.isArray(trip[k]) && trip[k].length > 0)) && (
                <div className="border-l-4 border-[#A855F7] bg-[#F3E8FF] pl-2.5 py-1.5 space-y-2 rounded-r-2xl" data-testid={`pilot-extra-stops-${trip.id}`}>
                  <p className="text-[9px] uppercase tracking-widest text-[#7C3AED] font-bold">Additional Stops · You can serve pickups and drops in any order</p>
                  {(trip.extra_pickups || []).map((es, i) => (
                    <div
                      key={es.id}
                      className="rounded-lg bg-white border border-[#00C853]/30 px-2 py-1.5 text-[9px] italic underline decoration-[#00C853]/40 underline-offset-2 space-y-1 text-[#0A0A0A]"
                      data-testid={`pilot-extra-pickup-${trip.id}-${i}`}
                    >
                      <div className="flex items-center justify-between gap-1 not-italic no-underline">
                        <span className="font-mono text-[#00C853] font-bold">PICKUP STOP {i + 2}</span>
                        {es.added_by_name && <span className="text-[#666]">by {es.added_by_name}</span>}
                      </div>
                      <p className="font-bold">{tr(es.supplier_name)}</p>
                      {(es.item || es.qty) && <p>{tr(es.item)}{es.qty ? ` · ${es.qty} ${tr(es.uom) || ''}` : ''}</p>}
                      {es.remarks && <p className="text-[#B27300] not-italic no-underline bg-[#FFF8E6] border border-[#FFD27F]/40 rounded px-1.5 py-1"><span className="font-bold mr-1">REMARK</span>{tr(es.remarks)}</p>}
                      {tab === 'in_progress' && (
                        <div className="flex gap-1.5 not-italic no-underline pt-1">
                          {!es.reached_at && (
                            <button onClick={(e) => { e.stopPropagation(); extraStopStep(trip.id, es.id, 'reached'); }} className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm" data-testid={`pickup-stop-${trip.id}-${i}-reached`}>Pickup Reached</button>
                          )}
                          {es.reached_at && !es.done_at && (() => {
                            const prox = proximityTo(es.lat, es.lng);
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); extraStopStep(trip.id, es.id, 'done'); }}
                                disabled={!prox.near}
                                title={!prox.near ? `Move closer — you're ${prox.hasFix ? Math.round(prox.distM) + 'm' : '?'} from this pickup (must be within ${GEOFENCE_M}m)` : 'Mark pickup done'}
                                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ${prox.near ? 'bg-[#FF8C00] text-white' : 'bg-[#CCCCCC] text-[#666] cursor-not-allowed'}`}
                                data-testid={`pickup-stop-${trip.id}-${i}-done`}
                              >{prox.near ? 'Pickup Done' : '🔒 Pickup Done'}</button>
                            );
                          })()}
                          {es.done_at && (
                            <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full">✓ {new Date(es.done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {(trip.extra_drops || []).map((es, i) => (
                    <div
                      key={es.id}
                      className="rounded-lg bg-white border border-[#FF3333]/30 px-2 py-1.5 text-[9px] italic underline decoration-[#FF3333]/40 underline-offset-2 space-y-1 text-[#0A0A0A]"
                      data-testid={`pilot-extra-drop-${trip.id}-${i}`}
                    >
                      <div className="flex items-center justify-between gap-1 not-italic no-underline">
                        <span className="font-mono text-[#FF3333] font-bold">DROP STOP {i + 2}</span>
                        {es.added_by_name && <span className="text-[#666]">by {es.added_by_name}</span>}
                      </div>
                      <p className="font-bold">{tr(es.supplier_name)}</p>
                      {(es.item || es.qty) && <p>{tr(es.item)}{es.qty ? ` · ${es.qty} ${tr(es.uom) || ''}` : ''}</p>}
                      {es.remarks && <p className="text-[#B27300] not-italic no-underline bg-[#FFF8E6] border border-[#FFD27F]/40 rounded px-1.5 py-1"><span className="font-bold mr-1">REMARK</span>{tr(es.remarks)}</p>}
                      {tab === 'in_progress' && (
                        <div className="flex gap-1.5 not-italic no-underline pt-1">
                          {!es.reached_at && (
                            <button onClick={(e) => { e.stopPropagation(); extraStopStep(trip.id, es.id, 'reached'); }} className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm" data-testid={`drop-stop-${trip.id}-${i}-reached`}>Drop Reached</button>
                          )}
                          {es.reached_at && !es.done_at && (() => {
                            const prox = proximityTo(es.lat, es.lng);
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); extraStopStep(trip.id, es.id, 'done'); }}
                                disabled={!prox.near}
                                title={!prox.near ? `Move closer — you're ${prox.hasFix ? Math.round(prox.distM) + 'm' : '?'} from this drop (must be within ${GEOFENCE_M}m)` : 'Mark drop done'}
                                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full shadow-sm ${prox.near ? 'bg-[#00C853] text-white' : 'bg-[#CCCCCC] text-[#666] cursor-not-allowed'}`}
                                data-testid={`drop-stop-${trip.id}-${i}-done`}
                              >{prox.near ? 'Drop Done' : '🔒 Drop Done'}</button>
                            );
                          })()}
                          {es.done_at && (
                            <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full">✓ {new Date(es.done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}


              {Array.isArray(trip.pooled_items) && trip.pooled_items.length > 0 && (
                <div
                  className="border-l-4 border-[#A855F7] bg-[#F3E8FF] pl-2.5 py-1.5 space-y-2 rounded-r-2xl"
                  data-testid={`trip-pooled-items-${trip.id}`}
                >
                  <p className="text-[9px] uppercase tracking-widest text-[#7C3AED] font-bold">
                    +{trip.pooled_items.length} Pooled bookings (italic = pooled)
                  </p>
                  {trip.pooled_items.map((it, i) => (
                    <div
                      key={it.id || i}
                      className="rounded-lg bg-white border border-[#A855F7]/30 px-2 py-1.5 text-[9px] italic underline decoration-[#A855F7]/40 underline-offset-2 space-y-1 text-[#5B21B6]"
                      data-testid={`pilot-pooled-item-${trip.id}-${i}`}
                    >
                      <div className="flex items-center justify-between gap-1 not-italic no-underline">
                        <span className="font-mono text-[#A855F7] font-bold">
                          #{it.booking_id || it.id?.slice(0, 8) || `pool-${i + 1}`}
                        </span>
                        {it.priority && (
                          <span className="text-[9px] rounded-full px-1.5 py-0.5 bg-[#808080]/10 text-[#666]">{it.priority}</span>
                        )}
                      </div>
                      {(it.pickup_supplier_name || it.drop_supplier_name) && (
                        <div className="flex items-start gap-1.5">
                          <div className="flex flex-col items-center pt-0.5 not-italic no-underline">
                            <div className="w-1.5 h-1.5 rounded-full bg-[#00C853]" />
                            <div className="w-px h-3 bg-[#E5E5E5]" />
                            <div className="w-1.5 h-1.5 rounded-full bg-[#FF3333]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate">{tr(it.pickup_supplier_name) || '—'}</p>
                            <p className="truncate mt-0.5">{tr(it.drop_supplier_name) || '—'}</p>
                          </div>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                        {it.item && <span>{tr(it.item)} · {it.qty ?? 0} {tr(it.uom) || ''}</span>}
                        {it.booked_by_name && (
                          <span>
                            By {tr(it.booked_by_name)}
                            {it.booked_by_mobile && (
                              <a
                                href={`tel:${it.booked_by_mobile}`}
                                className="ml-1 text-[#002FA7] not-italic no-underline font-mono"
                                onClick={(e) => e.stopPropagation()}
                              >
                                ☎ {it.booked_by_mobile}
                              </a>
                            )}
                          </span>
                        )}
                        {it.booked_from_halt && <span>From {tr(it.booked_from_halt)}</span>}
                      </div>
                      {it.remarks && (
                        <p className="text-[9px] not-italic no-underline text-[#B27300] bg-[#FFF8E6] border border-[#FFD27F]/40 rounded px-1.5 py-1 whitespace-pre-wrap">
                          <span className="font-bold mr-1">REMARK</span>{tr(it.remarks)}
                        </p>
                      )}
                      {/* Phase 78aa — Per-pooled-child progress buttons.
                          Shown ONLY while the lead is in_progress AND the
                          child's pickup/drop differs from the lead's
                          (same locations → same buttons as the lead, no
                          duplicate step needed). Buttons advance through
                          pickup_reached → pickup_done → drop_reached →
                          drop_done independently per child. */}
                      {tab === 'in_progress' && (() => {
                        const diffPickup = it.pickup_supplier_name && it.pickup_supplier_name !== trip.pickup_supplier_name;
                        const diffDrop = it.drop_supplier_name && it.drop_supplier_name !== trip.drop_supplier_name;
                        if (!diffPickup && !diffDrop) return null;
                        return (
                          <div className="flex flex-wrap gap-1.5 not-italic no-underline pt-1">
                            {diffPickup && !it.pickup_reached_at && (
                              <button
                                onClick={(e) => { e.stopPropagation(); pooledStep(trip.id, it.id || it.booking_id, 'pickup_reached'); }}
                                className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm hover:brightness-95"
                                data-testid={`pooled-${trip.id}-${i}-pickup-reached-btn`}
                              >
                                Pickup Reached
                              </button>
                            )}
                            {diffPickup && it.pickup_reached_at && !it.pickup_done_at && (
                              <button
                                onClick={(e) => { e.stopPropagation(); pooledStep(trip.id, it.id || it.booking_id, 'pickup_done'); }}
                                className="text-[10px] font-bold uppercase tracking-widest bg-[#FF8C00] text-white px-2.5 py-1 rounded-full shadow-sm hover:brightness-95"
                                data-testid={`pooled-${trip.id}-${i}-pickup-done-btn`}
                              >
                                Pickup Done
                              </button>
                            )}
                            {diffPickup && it.pickup_done_at && (
                              <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full">
                                Pickup ✓ {new Date(it.pickup_done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                              </span>
                            )}
                            {diffDrop && (!diffPickup || it.pickup_done_at) && !it.drop_reached_at && (
                              <button
                                onClick={(e) => { e.stopPropagation(); pooledStep(trip.id, it.id || it.booking_id, 'drop_reached'); }}
                                className="text-[10px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#0A0A0A] px-2.5 py-1 rounded-full shadow-sm hover:brightness-95"
                                data-testid={`pooled-${trip.id}-${i}-drop-reached-btn`}
                              >
                                Drop Reached
                              </button>
                            )}
                            {diffDrop && it.drop_reached_at && !it.drop_done_at && (
                              <button
                                onClick={(e) => { e.stopPropagation(); pooledStep(trip.id, it.id || it.booking_id, 'drop_done'); }}
                                className="text-[10px] font-bold uppercase tracking-widest bg-[#00C853] text-white px-2.5 py-1 rounded-full shadow-sm hover:brightness-95"
                                data-testid={`pooled-${trip.id}-${i}-drop-done-btn`}
                              >
                                Drop Done
                              </button>
                            )}
                            {diffDrop && it.drop_done_at && (
                              <span className="text-[9px] font-mono text-[#00C853] bg-[#00C853]/10 px-2 py-0.5 rounded-full">
                                Drop ✓ {new Date(it.drop_done_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              )}

              {/* Phase 78i — Google geofence-derived timings shown only on
                  the In Progress tab so the pilot can compare against
                  ground reality and decide when to tap manual buttons.
                  Each cell shows a single timestamp; missing values
                  render as a soft dash. */}
              {tab === 'in_progress' && (
                trip.google_start_time || trip.google_pickup_reach_time || trip.google_pickup_complete_time ||
                trip.google_drop_reach_time  || trip.google_drop_complete_time
              ) && (
                <div className="border border-[#002FA7]/20 bg-[#002FA7]/5 p-2 rounded-2xl" data-testid={`trip-google-timings-${trip.id}`}>
                  <p className="text-[9px] uppercase tracking-widest text-[#002FA7] font-bold mb-1.5">Google Timings <span className="text-[#666] font-normal">(geofence-derived)</span></p>
                  <div className="grid grid-cols-5 gap-1 text-[10px]">
                    {[
                      ['Start',        trip.google_start_time],
                      ['Pickup Reach', trip.google_pickup_reach_time],
                      ['Pickup Done',  trip.google_pickup_complete_time],
                      ['Drop Reach',   trip.google_drop_reach_time],
                      ['Drop Done',    trip.google_drop_complete_time],
                    ].map(([label, iso]) => (
                      <div key={label} className="text-center">
                        <p className="text-[#666]">{label}</p>
                        <p className={`font-mono font-bold mt-0.5 ${iso ? 'text-[#002FA7]' : 'text-[#999]'}`}>{iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Phase 78x — Booking staff remarks shown to pilot so any
                  special instructions (e.g., "fragile", "call before delivery",
                  "specific gate") aren't lost in chat. Yellow note style so
                  it's visually distinct from the contact strip below. */}
              {trip.remarks && trip.remarks.trim() && (
                <div
                  className="rounded-2xl border border-[#FFD27F] bg-[#FFF8E6] p-2 flex gap-2"
                  data-testid={`pilot-trip-${trip.id}-remarks`}
                >
                  <span className="text-[10px] uppercase tracking-widest text-[#B27300] font-bold whitespace-nowrap pt-0.5">
                    Remark
                  </span>
                  <p className="text-xs text-[#0A0A0A] whitespace-pre-wrap flex-1">
                    {tr(trip.remarks)}
                  </p>
                </div>
              )}

              {/* Direct-call contacts panel — pilot taps any number to dial. */}
              {(trip.booked_by_mobile || trip.pickup_user_mobile || trip.pickup_supplier_contact || trip.drop_supplier_contact) && (
                <div className="border border-[#E5E5E5] bg-[#F8F8F8] rounded-2xl p-2 space-y-1.5" data-testid={`pilot-trip-${trip.id}-contacts`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#666] font-bold">{t('contacts') || 'Contacts'}</div>
                  {trip.booked_by_mobile && (
                    <a href={`tel:${trip.booked_by_mobile}`} className="flex items-center justify-between gap-2 text-xs hover:bg-white px-1.5 py-1 rounded-lg" data-testid={`contact-booker-${trip.id}`}>
                      <span className="text-[#666]"><strong className="text-[#0A0A0A]">{t('booker') || 'Booker'}:</strong> {tr(trip.booked_by_name) || '—'}</span>
                      <span className="flex items-center gap-1 text-[#002FA7] font-mono whitespace-nowrap"><Phone size={11} /> {trip.booked_by_mobile}</span>
                    </a>
                  )}
                  {trip.pickup_user_mobile && (
                    <a href={`tel:${trip.pickup_user_mobile}`} className="flex items-center justify-between gap-2 text-xs hover:bg-white px-1.5 py-1 rounded-lg" data-testid={`contact-pickup-user-${trip.id}`}>
                      <span className="text-[#666]"><strong className="text-[#0A0A0A]">{t('pickup_user') || 'Pickup User'}:</strong> {tr(trip.pickup_user_name) || '—'}</span>
                      <span className="flex items-center gap-1 text-[#002FA7] font-mono whitespace-nowrap"><Phone size={11} /> {trip.pickup_user_mobile}</span>
                    </a>
                  )}
                  {trip.pickup_supplier_contact && (
                    <a href={`tel:${trip.pickup_supplier_contact}`} className="flex items-center justify-between gap-2 text-xs hover:bg-white px-1.5 py-1 rounded-lg" data-testid={`contact-pickup-supplier-${trip.id}`}>
                      <span className="text-[#666] truncate"><strong className="text-[#0A0A0A]">{t('pickup_supplier') || 'Pickup Supplier'}:</strong> {tr(trip.pickup_supplier_name) || '—'}</span>
                      <span className="flex items-center gap-1 text-[#002FA7] font-mono whitespace-nowrap"><Phone size={11} /> {trip.pickup_supplier_contact}</span>
                    </a>
                  )}
                  {trip.drop_supplier_contact && (
                    <a href={`tel:${trip.drop_supplier_contact}`} className="flex items-center justify-between gap-2 text-xs hover:bg-white px-1.5 py-1 rounded-lg" data-testid={`contact-drop-supplier-${trip.id}`}>
                      <span className="text-[#666] truncate"><strong className="text-[#0A0A0A]">{t('drop_supplier') || 'Drop Supplier'}:</strong> {tr(trip.drop_supplier_name) || '—'}</span>
                      <span className="flex items-center gap-1 text-[#002FA7] font-mono whitespace-nowrap"><Phone size={11} /> {trip.drop_supplier_contact}</span>
                    </a>
                  )}
                </div>
              )}

              {/* DC Photos strip — visible from allocated through drop_reached.
                  Phase 78i: hide on the Allocated tab to keep the card
                  compact; pilots only need Start there. */}
              {trip.status !== 'completed' && tab !== 'allocated' && (() => {
                const dcs = (trip.dc_photos && trip.dc_photos.length > 0)
                  ? trip.dc_photos.map(d => typeof d === 'string' ? d : d.url)
                  : (trip.dc_photo ? [trip.dc_photo] : []);
                const isPickup = !!trip.pickup_flow;
                return (
                  <div className="border border-[#E5E5E5] bg-[#F8F8F8] rounded-2xl p-2" data-testid={`pilot-trip-${trip.id}-dc-strip`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-[#666] font-bold">
                        {isPickup
                          ? (dcs.length > 0 ? `DC by Pickup · ${dcs.length}` : 'Awaiting DC')
                          : (dcs.length > 0 ? `DC · ${dcs.length}` : 'No DC yet')}
                      </span>
                      <button
                        onClick={() => setShowDcCamera({ tripId: trip.id, label: dcs.length === 0 ? 'Attach DC Photo' : 'Add another DC' })}
                        className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#002FA7] hover:underline rounded-full px-2 py-1"
                        data-testid={`pilot-trip-${trip.id}-add-dc-btn`}
                      >
                        <Plus size={12} /> Add DC
                      </button>
                    </div>
                    {dcs.length > 0 && (
                      <div className="flex gap-2 overflow-x-auto">
                        {dcs.map((url, idx) => (
                          <a
                            key={idx}
                            href={url.startsWith('/') ? `${API_BASE}${url}` : url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block flex-shrink-0"
                            data-testid={`pilot-trip-${trip.id}-dc-thumb-${idx}`}
                          >
                            <img
                              src={url.startsWith('/') ? `${API_BASE}${url}` : url}
                              alt={`DC ${idx + 1}`}
                              className="w-16 h-16 object-cover border border-[#E5E5E5]"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* KMS details for completed trips */}
              {trip.status === 'completed' && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-[#F7F7F7] p-2 text-center">
                      <p className="text-sm font-bold text-[#002FA7]">{trip.google_kms || '-'}</p>
                      <p className="text-[9px] uppercase tracking-widest text-[#666]">Google KMs</p>
                    </div>
                    <div className="bg-[#F7F7F7] p-2 text-center">
                      <p className="text-sm font-bold text-[#00C853]">{trip.manual_kms || ((trip.end_km && trip.start_km) ? (trip.end_km - trip.start_km) : '-')}</p>
                      <p className="text-[9px] uppercase tracking-widest text-[#666]">Manual KMs</p>
                    </div>
                  </div>
                  {/* Travelled route mini-map */}
                  <TripRouteMap tripId={trip.id} height="160px" compact={false} />
                </>
              )}

              {/* Delay reason badge */}
              {trip.pickup_delay_reason && (
                <div className="bg-[#FF8C00]/10 border border-[#FF8C00]/20 p-2 text-xs text-[#FF8C00] flex items-center gap-2">
                  <ClockCountdown size={14} /> {t('delay_reason')}: {t(trip.pickup_delay_reason)} {trip.pickup_delay_note ? `— ${trip.pickup_delay_note}` : ''}
                </div>
              )}

              {/* Phase 78i — progress strip hidden on Allocated tab
                  (pilot only sees Start there). */}
              {tab !== 'allocated' && (
                <div className="flex gap-1">
                  {STATUS_STEPS.map((step, idx) => {
                    const current = STATUS_STEPS.indexOf(trip.status);
                    return <div key={step} className={`h-1 flex-1 ${idx <= current ? 'bg-[#002FA7]' : 'bg-[#E5E5E5]'}`} />;
                  })}
                </div>
              )}

              {/* Action Buttons */}
              {nextAction && (
                <div className="space-y-2">
                  {/* Phase 72 — Warn pilot if trying to start a trip with no vehicle attached */}
                  {nextAction.action === 'start' && !vehicles.primary && !vehicles.secondary && (
                    <div
                      className="bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] text-[10px] font-bold uppercase tracking-widest px-2 py-1.5 rounded"
                      data-testid={`trip-no-vehicle-warning-${trip.id}`}
                    >
                      ⚠ No vehicle assigned — scan a vehicle QR first
                    </div>
                  )}
                  {/* Phase 78ar — Hide the legacy bottom action button on
                      multi-stop trips because the same actions are now
                      surfaced inline next to each stop above. The only
                      exception is the FINAL completion: when status =
                      drop_reached we still surface a single big "All
                      Completed" button at the bottom for muscle memory. */}
                  {(!hasExtras || (hasExtras && nextAction.action === 'drop-done')) && (() => {
                    // Phase 78as — Gate the bottom "All Completed" button (= drop-done) by drop geofence too.
                    const gateProx = hasExtras && nextAction.action === 'drop-done'
                      ? proximityTo(trip.drop_lat, trip.drop_lng)
                      : { near: true, distM: 0, hasFix: true };
                    const startDisabled = nextAction.action === 'start' && !vehicles.primary && !vehicles.secondary;
                    const finalDisabled = !gateProx.near;
                    const disabled = startDisabled || finalDisabled;
                    const label = hasExtras && nextAction.action === 'drop-done'
                      ? (gateProx.near ? 'All Completed' : `🔒 Move closer (${gateProx.hasFix ? Math.round(gateProx.distM) + 'm' : '?'} away)`)
                      : nextAction.label;
                    return (
                      <div className="flex gap-2">
                        <Button
                          onClick={() => requestConfirm(trip, nextAction)}
                          disabled={disabled}
                          className={`flex-1 ${disabled && finalDisabled && !startDisabled ? 'bg-[#CCCCCC]' : nextAction.color} text-white hover:opacity-90 rounded-full h-11 shadow-md disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-[#AAAAAA]`}
                          data-testid={`trip-action-${trip.id}`}
                        >
                          {label}
                        </Button>
                      </div>
                    );
                  })()}

                  {/* Undo / Reverse button — hidden on Allocated tab
                      where the only action is Start. */}
                  {tab !== 'allocated' && canReverse(trip.status) && (
                    <button
                      onClick={() => setConfirmDialog({ type: 'reverse', tripId: trip.id })}
                      className="w-full flex items-center justify-center gap-2 py-1.5 text-[10px] uppercase tracking-widest text-[#FF8C00] border border-[#FF8C00]/30 hover:bg-[#FF8C00]/5 transition-colors rounded-full"
                      data-testid={`undo-trip-${trip.id}`}
                    >
                      <ArrowCounterClockwise size={12} /> {t('undo_last')}
                    </button>
                  )}

                  {/* Reorder buttons for queued trips — Phase 78i: removed
                      from Allocated tab so admin-set priority is the
                      single source of truth. */}
                  {tab !== 'allocated' && isQueued && trips.filter(t => ['allocated', 'accepted'].includes(t.status)).length > 1 && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReorder(trip.id, 'up')}
                        disabled={idx === 0}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] disabled:opacity-30 rounded-full"
                        data-testid={`move-up-${trip.id}`}
                      >
                        <ArrowUp size={12} /> Move Up
                      </button>
                      <button
                        onClick={() => handleReorder(trip.id, 'down')}
                        disabled={idx === trips.filter(t => ['allocated', 'accepted'].includes(t.status)).length - 1}
                        className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] uppercase tracking-widest border border-[#E5E5E5] hover:bg-[#F3F3F3] disabled:opacity-30 rounded-full"
                        data-testid={`move-down-${trip.id}`}
                      >
                        <ArrowDown size={14} /> Move Down
                      </button>
                    </div>
                  )}

                  {/* Switch vehicle button — only when secondary attached & trip not finished */}
                  {vehicles.secondary && trip.status !== 'completed' && trip.status !== 'cancelled' && (() => {
                    const onSec = trip.vehicle_id === vehicles.secondary.id;
                    const target = onSec ? 'primary' : 'secondary';
                    const targetVnum = onSec ? (vehicles.primary?.vehicle_number || 'Veh 1') : vehicles.secondary.vehicle_number;
                    return (
                      <button
                        type="button"
                        onClick={() => handleSwitchVehicle(trip.id, target)}
                        className="w-full flex items-center justify-center gap-2 py-2 text-[11px] uppercase tracking-widest font-bold text-[#002FA7] border border-[#002FA7]/30 rounded-full hover:bg-[#002FA7]/5"
                        data-testid={`switch-vehicle-${trip.id}`}
                      >
                        ⇄ Move to {onSec ? 'Veh 1' : 'Veh 2'} · {targetVnum}
                      </button>
                    );
                  })()}
                </div>
              )}
            </div>
          );
        });
        })()}
        {trips.length === 0 && (
          <div className="text-center py-12 text-[#666]">
            <MapPin size={48} className="mx-auto mb-4 text-[#E5E5E5]" />
            <p className="text-sm">{t('no_data')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
